import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HomeIcon, SearchIcon, PawIcon, VolunteerIcon, ChatIcon } from './Icons';
import pb from '../services/pocketbase';
import '../styles/BottomNav.css';

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [badges, setBadges] = useState({ lost: 0, found: 0, chats: 0 });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [currentUser, setCurrentUser] = useState(pb.authStore.model);

  useEffect(() => {
    const unsubscribe = pb.authStore.onChange((token, model) => {
      setCurrentUser(model);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 15000);
    return () => clearInterval(interval);
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBadges = async () => {
    if (!currentUser?.id) {
      setBadges({ lost: 0, found: 0, chats: 0 });
      return;
    }
    const lastLost = localStorage.getItem('lastVisitLost') || '2000-01-01';
    const lastFound = localStorage.getItem('lastVisitFound') || '2000-01-01';
    try {
      const msgs = await pb.collection('messages').getList(1, 200, {
        filter: `userId != "${currentUser.id}" && chatType = "private"`,
        fields: 'id,chatId,read'
      });
      const unreadChats = msgs.items.filter(m => m.chatId && m.chatId.includes(currentUser.id) && !m.read).length;

      const [lostRes, foundRes] = await Promise.all([
        pb.collection('posts').getList(1, 1, { filter: `type = "lost" && created > "${lastLost}" && userId != "${currentUser.id}"`, fields: 'id' }),
        pb.collection('posts').getList(1, 1, { filter: `type = "found" && created > "${lastFound}" && userId != "${currentUser.id}"`, fields: 'id' }),
      ]);

      setBadges({ lost: lostRes.totalItems, found: foundRes.totalItems, chats: unreadChats });
    } catch (e) {}
  };

  // Набор кнопок меню по типу пользователя
  const getFabMenuItems = () => {
    const userRole = currentUser?.userRole;
    const isVolunteer = userRole === 'volunteer' || userRole === 'volunteer_pending';
    const isService = userRole === 'service';

    if (isService) {
      return [
        { label: 'Услуга', emoji: '🛎️', path: '/create-post?type=service', color: '#3B5998' },
        { label: 'Нашёлся', emoji: '✅', path: '/create-post?type=found', color: '#4CAF50' },
        { label: 'Потерялся', emoji: '🐾', path: '/create-post?type=lost', color: '#FF6B35' },
      ];
    }

    if (isVolunteer) {
      return [
        { label: 'Нужна помощь', emoji: '🆘', path: '/create-post?type=help', color: '#E53935' },
        { label: 'Нашёлся', emoji: '✅', path: '/create-post?type=found', color: '#4CAF50' },
        { label: 'Потерялся', emoji: '🐾', path: '/create-post?type=lost', color: '#FF6B35' },
      ];
    }

    // Обычный пользователь
    return [
      { label: 'Нашёлся', emoji: '✅', path: '/create-post?type=found', color: '#4CAF50' },
      { label: 'Потерялся', emoji: '🐾', path: '/create-post?type=lost', color: '#FF6B35' },
    ];
  };

  const handleFabClick = () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    setShowFabMenu(prev => !prev);
  };

  const handleFabMenuSelect = (path) => {
    setShowFabMenu(false);
    navigate(path);
  };

  const handleNav = (path) => {
    setShowFabMenu(false);
    const protectedPaths = ['/chats', '/profile', '/create-post'];
    if (!currentUser && protectedPaths.some(p => path.startsWith(p))) {
      setShowAuthModal(true);
      return;
    }
    if (path === '/volunteers') localStorage.setItem('lastVisitVolunteers', new Date().toISOString());
    if (path === '/services') localStorage.setItem('lastVisitServices', new Date().toISOString());
    navigate(path);
  };

  const navItems = [
    { path: '/home', icon: HomeIcon, label: 'Главная' },
    { path: '/create-post?type=lost', icon: SearchIcon, label: 'Потерял' },
    { path: '/create-post?type=found', icon: PawIcon, label: 'Нашёл' },
    { path: '/volunteers', icon: VolunteerIcon, label: 'Волонтёры' },
    { path: '/chats', icon: ChatIcon, label: 'Чаты', badge: badges.chats },
    { path: '/services', icon: () => <span style={{fontSize:'22px'}}>🛎️</span>, label: 'Услуги' },
  ];

  const isActive = (path) => {
    if (path === '/create-post?type=lost' || path === '/create-post?type=found') return location.pathname === '/create-post';
    if (path === '/chats') return location.pathname === '/chats' || location.pathname.startsWith('/chat/');
    if (path === '/volunteers') return location.pathname.startsWith('/volunteers');
    if (path === '/services') return location.pathname.startsWith('/services');
    return location.pathname === path;
  };

  const fabMenuItems = currentUser ? getFabMenuItems() : [];

  return (
    <>
      {/* Затемнение фона при открытом FAB меню */}
      {showFabMenu && (
        <div
          onClick={() => setShowFabMenu(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 99,
          }}
        />
      )}

      {/* FAB меню — кнопки над FAB */}
      {showFabMenu && (
        <div style={{
          position: 'fixed', bottom: '148px', right: '16px',
          display: 'flex', flexDirection: 'column', gap: '10px',
          zIndex: 101,
          animation: 'fabMenuIn 0.18s ease',
        }}>
          <style>{`
            @keyframes fabMenuIn {
              from { opacity: 0; transform: translateY(12px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {fabMenuItems.map((item) => (
            <div
              key={item.path}
              onClick={() => handleFabMenuSelect(item.path)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'white', borderRadius: '24px',
                padding: '10px 16px 10px 12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: item.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', flexShrink: 0,
              }}>{item.emoji}</div>
              <span style={{ fontWeight: '600', fontSize: '15px', color: '#222' }}>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* FAB кнопка */}
      <div
        onClick={handleFabClick}
        style={{
          position: 'fixed', bottom: '80px', right: '20px',
          width: '56px', height: '56px', borderRadius: '50%',
          background: showFabMenu
            ? 'linear-gradient(135deg, #666, #888)'
            : 'linear-gradient(135deg, #FF6B35, #FF8C42)',
          color: 'white', fontSize: '28px', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(255,107,53,0.4)',
          cursor: 'pointer', zIndex: 102,
          transition: 'transform 0.2s, background 0.2s',
          transform: showFabMenu ? 'rotate(45deg)' : 'rotate(0deg)',
        }}
        onMouseDown={e => e.currentTarget.style.transform = showFabMenu ? 'rotate(45deg) scale(0.92)' : 'scale(0.92)'}
        onMouseUp={e => e.currentTarget.style.transform = showFabMenu ? 'rotate(45deg)' : 'scale(1)'}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 4V20M4 12H20" stroke="white" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      <div className="bottom-nav">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <div
              key={item.path}
              className={'nav-item ' + (active ? 'active' : '')}
              onClick={() => handleNav(item.path)}
            >
              <div className="nav-icon" style={{position:'relative'}}>
                <Icon active={active} />
                {item.badge > 0 && (
                  <div className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</div>
                )}
              </div>
              <div className="nav-label">{item.label}</div>
            </div>
          );
        })}
      </div>

      {/* Модалка авторизации для гостей */}
      {showAuthModal && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end'}}
          onClick={() => setShowAuthModal(false)}>
          <div style={{background:'white', borderRadius:'24px 24px 0 0', width:'100%', padding:'24px'}}
            onClick={e => e.stopPropagation()}>
            <div style={{textAlign:'center', marginBottom:'20px'}}>
              <div style={{fontSize:'40px', marginBottom:'8px'}}>🐾</div>
              <div style={{fontWeight:'700', fontSize:'18px', marginBottom:'6px'}}>Нужна авторизация</div>
              <div style={{fontSize:'14px', color:'#666'}}>Войдите или зарегистрируйтесь чтобы продолжить</div>
            </div>
            <div onClick={() => navigate('/login')}
              style={{display:'block', padding:'14px', background:'#3B5998', color:'white', borderRadius:'12px', textAlign:'center', fontSize:'15px', fontWeight:'700', cursor:'pointer', marginBottom:'10px'}}>
              Войти
            </div>
            <div onClick={() => navigate('/register')}
              style={{display:'block', padding:'14px', background:'#F5F9FF', color:'#3B5998', borderRadius:'12px', textAlign:'center', fontSize:'15px', fontWeight:'700', cursor:'pointer', border:'2px solid #E3F2FD'}}>
              Зарегистрироваться
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BottomNav;
